/**
 * Image Search Types
 * 
 * Types for image similarity search feature using GPT-5 Vision API
 * to generate keywords and perform RAG-based search.
 */

/**
 * Configuration options for image search
 */
export interface ImageSearchConfig {
  detailLevel?: 'low' | 'high' | 'auto'; // Image analysis detail level (default: 'high')
}

/**
 * WebSocket message payload for IMAGE_SEARCH requests
 * Matches backend ImageSearchRequest model
 */
export interface ImageSearchRequest {
  type: 'IMAGE_SEARCH';
  image: string;             // Base64 encoded image data
  config?: ImageSearchConfig;
  messageId?: string;
  conversationId?: string;
  caption?: string;          // Optional user-provided caption
}

/**
 * Image search validation constraints
 * Should match backend configuration
 */
export const IMAGE_SEARCH_CONSTRAINTS = {
  MAX_SIZE_BYTES: 5 * 1024 * 1024, // 5MB
  SUPPORTED_FORMATS: ['jpg', 'jpeg', 'png', 'webp', 'gif'],
  MAX_IMAGES_PER_MESSAGE: 1,
  DEFAULT_DETAIL_LEVEL: 'high' as const,
  // DEFAULT_MAX_KEYWORDS removed - now comes from backend config
};

/**
 * Helper function to validate image file
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file size
  if (file.size > IMAGE_SEARCH_CONSTRAINTS.MAX_SIZE_BYTES) {
    return {
      valid: false,
      error: `Image size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (5MB)`,
    };
  }

  // Check file format
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension || !IMAGE_SEARCH_CONSTRAINTS.SUPPORTED_FORMATS.includes(extension)) {
    return {
      valid: false,
      error: `Image format '${extension}' is not supported. Supported formats: ${IMAGE_SEARCH_CONSTRAINTS.SUPPORTED_FORMATS.join(', ')}`,
    };
  }

  return { valid: true };
}

/**
 * Helper function to convert File to base64
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove data URL prefix (e.g., "data:image/png;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to read file as base64'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
